import { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Camera, MapPin, PackageCheck, Truck } from 'lucide-react';
import type { CaseActor, CaseStatus, ReturnMethod } from '../../shared/case-schema.js';
import type { LostDate, ReportPlace } from '../../shared/report-schema.js';
import { api } from '../api.js';
import { AppShell } from '../components/AppShell.js';
import { StatusBadge, statusLabel } from '../components/StatusBadge.js';
import { Button, Field, Input, Loading, Notice, Textarea } from '../components/ui.js';

interface VenueCase {
  reference: string;
  venue: { name: string; city: string };
  item: {
    description: string;
    category: string;
    color: string;
    brand: string;
    distinguishingFeatures?: string;
  };
  lostDate: LostDate;
  place: ReportPlace;
  proof: { description?: string; photoAvailable: boolean };
  status: CaseStatus;
  version: number;
  history: Array<{
    id: string;
    at: string;
    actor: CaseActor;
    action: string;
    toStatus: CaseStatus;
    publicMessage?: string;
    privateNote?: string;
  }>;
  returnMethod?: ReturnMethod;
  shipping?: {
    recipientName: string;
    address: string;
    phone?: string;
    instructions?: string;
  };
  pickupInstructions?: string;
  shipment?: { carrier: string; trackingNumber: string; trackingUrl?: string };
}

export function VenueCasePage() {
  const [caseRecord, setCaseRecord] = useState<VenueCase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setCaseRecord(await api<VenueCase>('/api/venue/case'));
      setError(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'The case is unavailable');
    }
  }, []);
  useEffect(() => void load(), [load]);

  if (!caseRecord) {
    return (
      <AppShell language="en">
        {error ? (
          <section className="center-card">
            <h1>Venue case unavailable</h1>
            <Notice tone="danger">{error}</Notice>
          </section>
        ) : (
          <Loading label="Loading the secure venue case..." />
        )}
      </AppShell>
    );
  }

  return (
    <AppShell language="en">
      <div className="case-layout venue-case-layout">
        <header className="case-header">
          <div>
            <span className="eyebrow">Venue case · Report {caseRecord.reference}</span>
            <h1>{statusLabel(caseRecord.status)}</h1>
            <p>
              {caseRecord.venue.name}, {caseRecord.venue.city}
            </p>
          </div>
          <StatusBadge status={caseRecord.status} />
        </header>

        <section className="flow-card venue-report-summary">
          <h2>Lost item report</h2>
          <dl className="case-details">
            <div>
              <dt>
                <PackageCheck aria-hidden /> Item
              </dt>
              <dd>
                <strong>{caseRecord.item.description}</strong>
                <span>
                  {caseRecord.item.category} · {caseRecord.item.color} · {caseRecord.item.brand}
                </span>
                {caseRecord.item.distinguishingFeatures ? (
                  <span>{caseRecord.item.distinguishingFeatures}</span>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>
                <CalendarDays aria-hidden /> Date
              </dt>
              <dd>{dateLabel(caseRecord.lostDate)}</dd>
            </div>
            <div>
              <dt>
                <MapPin aria-hidden /> Place
              </dt>
              <dd>{placeLabel(caseRecord.place)}</dd>
            </div>
            <div>
              <dt>
                <Camera aria-hidden /> Private proof
              </dt>
              <dd>{caseRecord.proof.description ?? 'No proof description provided'}</dd>
            </div>
          </dl>
          {caseRecord.proof.photoAvailable ? (
            <img className="venue-proof-photo" src="/api/venue/case/photo" alt="Private proof" />
          ) : null}
        </section>

        <VenueNextAction caseRecord={caseRecord} onSaved={setCaseRecord} reload={load} />
        <InternalNoteForm caseRecord={caseRecord} onSaved={setCaseRecord} reload={load} />

        <section className="flow-card">
          <h2>Case activity</h2>
          <ol className="venue-history">
            {[...caseRecord.history].reverse().map((entry) => (
              <li key={entry.id}>
                <div>
                  <strong>{entry.publicMessage ?? entry.action.replaceAll('_', ' ')}</strong>
                  <span>
                    {entry.actor.toLowerCase()} · {new Date(entry.at).toLocaleString('en')}
                  </span>
                </div>
                {entry.privateNote ? <p>Private note: {entry.privateNote}</p> : null}
              </li>
            ))}
          </ol>
        </section>
      </div>
    </AppShell>
  );
}

function VenueNextAction({
  caseRecord,
  onSaved,
  reload,
}: {
  caseRecord: VenueCase;
  onSaved: (value: VenueCase) => void;
  reload: () => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = async (nextStatus: CaseStatus, terminal = false) => {
    if (terminal && !window.confirm('Confirm this case update?')) return;
    setSaving(true);
    setError(null);
    try {
      onSaved(
        await api<VenueCase>('/api/venue/case/status', {
          method: 'POST',
          body: JSON.stringify({ expectedVersion: caseRecord.version, nextStatus }),
        }),
      );
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not update the case');
      await reload();
    } finally {
      setSaving(false);
    }
  };

  if (caseRecord.status === 'RETURN_DETAILS_PROVIDED') {
    return <FulfillmentForm caseRecord={caseRecord} onSaved={onSaved} reload={reload} />;
  }
  if (caseRecord.status === 'RETURNED' || caseRecord.status === 'CLOSED_NOT_FOUND') {
    return (
      <section className="flow-card case-next-action">
        <PackageCheck aria-hidden />
        <div>
          <h2>This case is complete</h2>
          <p>No further venue action is required.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="flow-card venue-actions-card">
      <span className="eyebrow">Next action</span>
      <h2>Update the case</h2>
      {caseRecord.status === 'POSSIBLE_MATCH' ? (
        <Notice tone="info">Waiting for the guest to choose shipping or venue pickup.</Notice>
      ) : null}
      {caseRecord.status === 'SHIPPED' ? (
        <Notice tone="info">The guest can confirm receipt from their tracking page.</Notice>
      ) : null}
      {caseRecord.status === 'READY_FOR_PICKUP' ? (
        <Notice tone="info">Mark returned after the guest collects the item.</Notice>
      ) : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="venue-action-buttons">
        {caseRecord.status === 'SUBMITTED' ? (
          <Button variant="secondary" disabled={saving} onClick={() => void status('REVIEWING')}>
            Start review
          </Button>
        ) : null}
        {['SUBMITTED', 'REVIEWING'].includes(caseRecord.status) ? (
          <Button disabled={saving} onClick={() => void status('POSSIBLE_MATCH')}>
            Mark possible match
          </Button>
        ) : null}
        {caseRecord.status === 'POSSIBLE_MATCH' ? (
          <Button variant="secondary" disabled={saving} onClick={() => void status('REVIEWING')}>
            Return to review
          </Button>
        ) : null}
        {['SUBMITTED', 'REVIEWING', 'POSSIBLE_MATCH'].includes(caseRecord.status) ? (
          <Button
            variant="danger"
            disabled={saving}
            onClick={() => void status('CLOSED_NOT_FOUND', true)}
          >
            Close as not found
          </Button>
        ) : null}
        {['SHIPPED', 'READY_FOR_PICKUP'].includes(caseRecord.status) ? (
          <Button disabled={saving} onClick={() => void status('RETURNED', true)}>
            Mark item returned
          </Button>
        ) : null}
      </div>
    </section>
  );
}

function FulfillmentForm({
  caseRecord,
  onSaved,
  reload,
}: {
  caseRecord: VenueCase;
  onSaved: (value: VenueCase) => void;
  reload: () => Promise<void>;
}) {
  const [carrier, setCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [pickupInstructions, setPickupInstructions] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shipping = caseRecord.returnMethod === 'SHIP';
  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = shipping
        ? {
            expectedVersion: caseRecord.version,
            action: 'SHIP',
            carrier,
            trackingNumber,
            ...(trackingUrl.trim() ? { trackingUrl } : {}),
          }
        : { expectedVersion: caseRecord.version, action: 'PICKUP', pickupInstructions };
      onSaved(
        await api<VenueCase>('/api/venue/case/fulfillment', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      );
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not save fulfillment');
      await reload();
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="flow-card fulfillment-form">
      <span className="eyebrow">Guest instructions received</span>
      <h2>{shipping ? 'Arrange shipment' : 'Prepare venue pickup'}</h2>
      {shipping && caseRecord.shipping ? (
        <div className="shipping-address">
          <Truck aria-hidden />
          <div>
            <strong>{caseRecord.shipping.recipientName}</strong>
            <p>{caseRecord.shipping.address}</p>
            {caseRecord.shipping.phone ? <p>Phone: {caseRecord.shipping.phone}</p> : null}
            {caseRecord.shipping.instructions ? (
              <p>Instructions: {caseRecord.shipping.instructions}</p>
            ) : null}
          </div>
        </div>
      ) : null}
      {shipping ? (
        <>
          <Field label="Carrier or postal service">
            <Input value={carrier} onChange={(event) => setCarrier(event.target.value)} />
          </Field>
          <Field label="Tracking number">
            <Input
              value={trackingNumber}
              onChange={(event) => setTrackingNumber(event.target.value)}
            />
          </Field>
          <Field label="Tracking URL, optional">
            <Input
              type="url"
              value={trackingUrl}
              onChange={(event) => setTrackingUrl(event.target.value)}
            />
          </Field>
        </>
      ) : (
        <Field label="Guest-visible pickup instructions">
          <Textarea
            value={pickupInstructions}
            onChange={(event) => setPickupInstructions(event.target.value)}
          />
        </Field>
      )}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="flow-actions single">
        <Button
          disabled={
            saving ||
            (shipping ? !carrier.trim() || !trackingNumber.trim() : !pickupInstructions.trim())
          }
          onClick={() => void submit()}
        >
          {saving ? 'Saving...' : shipping ? 'Mark as shipped' : 'Mark ready for pickup'}
        </Button>
      </div>
    </section>
  );
}

function InternalNoteForm({
  caseRecord,
  onSaved,
  reload,
}: {
  caseRecord: VenueCase;
  onSaved: (value: VenueCase) => void;
  reload: () => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      onSaved(
        await api<VenueCase>('/api/venue/case/note', {
          method: 'POST',
          body: JSON.stringify({ expectedVersion: caseRecord.version, note }),
        }),
      );
      setNote('');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not save note');
      await reload();
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="flow-card">
      <h2>Private venue note</h2>
      <p>Private notes are visible to venue staff and Refindi administrators, not the guest.</p>
      <Field label="Note">
        <Textarea value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="flow-actions single">
        <Button variant="secondary" disabled={saving || !note.trim()} onClick={() => void save()}>
          {saving ? 'Saving...' : 'Add private note'}
        </Button>
      </div>
    </section>
  );
}

function dateLabel(date: LostDate): string {
  return date.kind === 'KNOWN'
    ? date.start === date.end
      ? date.start
      : `${date.start} to ${date.end}`
    : 'Unknown';
}

function placeLabel(place: ReportPlace): string {
  return place.kind === 'UNKNOWN'
    ? 'Unknown'
    : `${place.name}${place.context ? ` · ${place.context}` : ''}`;
}
